import {
  ElementRef,
  ElementType,
  ForwardedRef,
  forwardRef,
  ReactNode,
} from "react"
import { css, cx } from "@emotion/css"
import type {
  PolymorphicForwardRefExoticComponent,
  PolymorphicPropsWithoutRef,
  PolymorphicPropsWithRef,
} from "../utils/react-polymorphic-types"

const DefaultElement = "li"

const styles = {
  item: css`
    --highlight-color: var(--primary-light, rgba(0, 0, 0, 0.04));
    min-height: 32px;
    width: 100%;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 10px;
    padding-right: 10px;

    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    text-align: left;
    text-decoration: none;
    outline: none;
    user-select: none;
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    cursor: pointer;

    font-size: 13px;
    line-height: 1;
    color: var(--black, #000);
  `,
  itemDisabled: css`
    cursor: default;
    opacity: 0.4;
    pointer-events: none;
  `,
  itemHighlighted: css`
    background-color: var(--highlight-color);
  `,
}

type OptionOwnProps = {
  startIcon?: ReactNode
  //
  disabled?: boolean
  highlighted?: boolean
}

export type OptionProps<T extends ElementType = typeof DefaultElement> =
  PolymorphicPropsWithRef<OptionOwnProps, T>

const Option: PolymorphicForwardRefExoticComponent<
  OptionOwnProps,
  typeof DefaultElement
> = forwardRef(function Option<T extends ElementType = typeof DefaultElement>(
  props: PolymorphicPropsWithoutRef<OptionOwnProps, T>,
  ref: ForwardedRef<ElementRef<T>>,
) {
  const {
    as,
    //
    children,
    className,
    //
    disabled,
    highlighted,
    //
    ...rest
  } = props

  const Element: ElementType = as || DefaultElement
  const cn = cx(
    "__option",
    styles.item,
    {
      [styles.itemHighlighted]: highlighted,
      [styles.itemDisabled]: disabled,
    },
    className,
  )

  return (
    <Element ref={ref} className={cn} role="option" {...rest}>
      {children}
    </Element>
  )
})

export default Option
